import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Upload, 
  FileText, 
  MessageSquare, 
  Briefcase, 
  User,
  Send,
  Bell,
  Sun,
  Moon,
  Languages,
  LogOut,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  Scale
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientPortalPage = () => {
  const { t, language, toggleLanguage, direction } = useLanguage();
  const { user, token, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [cases, setCases] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedContact, setSelectedContact] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedContact) {
      fetchConversation(selectedContact.id);
    }
  }, [selectedContact]);

  const fetchData = async () => {
    try {
      const [casesRes, docsRes, contactsRes, inboxRes, unreadRes] = await Promise.all([
        api.get('/portal/my-cases'),
        api.get('/portal/my-documents'),
        api.get('/portal/contacts'),
        api.get('/messages/inbox'),
        api.get('/messages/unread/count')
      ]);
      setCases(casesRes.data);
      setDocuments(docsRes.data);
      setContacts(contactsRes.data);
      setMessages(inboxRes.data);
      setUnreadCount(unreadRes.data.count);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (userId) => {
    try {
      const res = await api.get(`/messages/conversation/${userId}`);
      setConversation(res.data);
    } catch (error) {
      console.error('Conversation fetch error:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedContact) return;
    
    try {
      await api.post('/messages', {
        receiver_id: selectedContact.id,
        subject: language === 'ar' ? 'رسالة جديدة' : 'New Message',
        content: newMessage
      });
      setNewMessage('');
      fetchConversation(selectedContact.id);
      toast.success(language === 'ar' ? 'تم إرسال الرسالة' : 'Message sent');
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل الإرسال' : 'Failed to send');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error(language === 'ar' ? 'اختر ملف' : 'Select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    if (uploadDescription) {
      formData.append('description', uploadDescription);
    }

    try {
      await api.post('/portal/upload-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(language === 'ar' ? 'تم رفع الملف بنجاح' : 'File uploaded successfully');
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadDescription('');
      fetchData();
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل الرفع' : 'Upload failed');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { class: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400', label: language === 'ar' ? 'نشط' : 'Active' },
      pending: { class: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400', label: language === 'ar' ? 'معلق' : 'Pending' },
      closed: { class: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400', label: language === 'ar' ? 'مغلق' : 'Closed' },
      pending_review: { class: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', label: language === 'ar' ? 'قيد المراجعة' : 'Under Review' }
    };
    const s = statusMap[status] || statusMap.pending;
    return <Badge className={s.class}>{s.label}</Badge>;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" dir={direction} data-testid="client-portal">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[hsl(222,47%,11%)] text-white shadow-lg">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-[hsl(var(--accent))] flex items-center justify-center">
                <Scale className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-bold text-lg">
                  {language === 'ar' ? 'بوابة العميل' : 'Client Portal'}
                </h1>
                <p className="text-xs text-white/60">{user?.full_name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative text-white hover:bg-white/10">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>
              
              {/* Language */}
              <Button variant="ghost" size="sm" onClick={toggleLanguage} className="text-white hover:bg-white/10">
                <Languages className="w-4 h-4 mr-1" />
                {language === 'en' ? 'العربية' : 'EN'}
              </Button>
              
              {/* Theme */}
              <Button variant="ghost" size="icon" onClick={toggleTheme} className="text-white hover:bg-white/10">
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              
              {/* Logout */}
              <Button variant="ghost" size="icon" onClick={logout} className="text-red-400 hover:bg-red-500/20">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-lg mx-auto">
            <TabsTrigger value="dashboard" className="gap-2">
              <Briefcase className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'ar' ? 'الرئيسية' : 'Home'}</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'ar' ? 'الملفات' : 'Files'}</span>
            </TabsTrigger>
            <TabsTrigger value="messages" className="gap-2 relative">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'ar' ? 'الرسائل' : 'Messages'}</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="profile" className="gap-2">
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'ar' ? 'حسابي' : 'Profile'}</span>
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Welcome Card */}
            <Card className="bg-gradient-to-r from-[hsl(222,47%,11%)] to-[hsl(222,47%,20%)] text-white">
              <CardContent className="py-6">
                <h2 className="text-2xl font-bold mb-2">
                  {language === 'ar' ? `مرحباً، ${user?.full_name}` : `Welcome, ${user?.full_name}`}
                </h2>
                <p className="text-white/70">
                  {language === 'ar' 
                    ? 'يمكنك متابعة قضاياك ورفع المستندات والتواصل مع محاميك من هنا'
                    : 'Track your cases, upload documents, and communicate with your lawyer here'}
                </p>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6 text-center">
                  <Briefcase className="w-8 h-8 mx-auto text-[hsl(var(--accent))] mb-2" />
                  <p className="text-2xl font-bold">{cases.length}</p>
                  <p className="text-sm text-muted-foreground">{language === 'ar' ? 'قضاياي' : 'My Cases'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <FileText className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                  <p className="text-2xl font-bold">{documents.length}</p>
                  <p className="text-sm text-muted-foreground">{language === 'ar' ? 'المستندات' : 'Documents'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <MessageSquare className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                  <p className="text-2xl font-bold">{messages.length}</p>
                  <p className="text-sm text-muted-foreground">{language === 'ar' ? 'الرسائل' : 'Messages'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <Bell className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                  <p className="text-2xl font-bold">{unreadCount}</p>
                  <p className="text-sm text-muted-foreground">{language === 'ar' ? 'غير مقروءة' : 'Unread'}</p>
                </CardContent>
              </Card>
            </div>

            {/* My Cases */}
            <Card>
              <CardHeader>
                <CardTitle>{language === 'ar' ? 'قضاياي' : 'My Cases'}</CardTitle>
              </CardHeader>
              <CardContent>
                {cases.length > 0 ? (
                  <div className="space-y-3">
                    {cases.map(c => (
                      <div key={c.id} className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-sm bg-[hsl(var(--accent))]/10">
                            <Briefcase className="w-5 h-5 text-[hsl(var(--accent))]" />
                          </div>
                          <div>
                            <p className="font-medium">{c.title}</p>
                            <p className="text-sm text-muted-foreground">{c.case_number}</p>
                          </div>
                        </div>
                        {getStatusBadge(c.status)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{language === 'ar' ? 'لا توجد قضايا حالياً' : 'No cases yet'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">{language === 'ar' ? 'المستندات' : 'Documents'}</h2>
              <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-accent gap-2">
                    <Upload className="w-4 h-4" />
                    {language === 'ar' ? 'رفع ملف' : 'Upload File'}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{language === 'ar' ? 'رفع مستند جديد' : 'Upload New Document'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>{language === 'ar' ? 'الملف' : 'File'}</Label>
                      <Input
                        ref={fileInputRef}
                        type="file"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                        className="cursor-pointer"
                      />
                      {uploadFile && (
                        <p className="text-sm text-muted-foreground">
                          {uploadFile.name} ({formatFileSize(uploadFile.size)})
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'ar' ? 'وصف (اختياري)' : 'Description (optional)'}</Label>
                      <Textarea
                        value={uploadDescription}
                        onChange={(e) => setUploadDescription(e.target.value)}
                        rows={2}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                      </Button>
                      <Button className="btn-accent" onClick={handleUpload}>
                        <Upload className="w-4 h-4 mr-2" />
                        {language === 'ar' ? 'رفع' : 'Upload'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {documents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map(doc => (
                  <Card key={doc.id}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-sm bg-muted">
                          <FileText className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{doc.filename}</p>
                          <p className="text-sm text-muted-foreground">{formatFileSize(doc.file_size)}</p>
                          <div className="mt-2">
                            {getStatusBadge(doc.status || 'pending_review')}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{language === 'ar' ? 'لا توجد مستندات' : 'No documents'}</p>
              </Card>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-6">
            <Card className="h-[600px] flex flex-col">
              <CardHeader className="border-b">
                <CardTitle>{language === 'ar' ? 'الرسائل' : 'Messages'}</CardTitle>
              </CardHeader>
              <div className="flex flex-1 overflow-hidden">
                {/* Contacts List */}
                <div className="w-1/3 border-r overflow-y-auto">
                  {contacts.length > 0 ? (
                    contacts.map(contact => (
                      <div
                        key={contact.id}
                        onClick={() => setSelectedContact(contact)}
                        className={`p-4 border-b cursor-pointer hover:bg-muted/50 transition-colors ${
                          selectedContact?.id === contact.id ? 'bg-muted' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{contact.full_name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{contact.role}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-center text-muted-foreground">
                      {language === 'ar' ? 'لا توجد جهات اتصال' : 'No contacts'}
                    </div>
                  )}
                </div>

                {/* Conversation */}
                <div className="flex-1 flex flex-col">
                  {selectedContact ? (
                    <>
                      {/* Chat Header */}
                      <div className="p-4 border-b bg-muted/30">
                        <p className="font-medium">{selectedContact.full_name}</p>
                        <p className="text-sm text-muted-foreground capitalize">{selectedContact.role}</p>
                      </div>

                      {/* Messages */}
                      <ScrollArea className="flex-1 p-4">
                        <div className="space-y-4">
                          {conversation.map(msg => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[70%] p-3 rounded-lg ${
                                  msg.sender_id === user?.id
                                    ? 'bg-[hsl(var(--accent))] text-white'
                                    : 'bg-muted'
                                }`}
                              >
                                <p className="text-sm">{msg.content}</p>
                                <p className={`text-xs mt-1 ${
                                  msg.sender_id === user?.id ? 'text-white/70' : 'text-muted-foreground'
                                }`}>
                                  {new Date(msg.created_at).toLocaleTimeString(language === 'ar' ? 'ar-IQ' : 'en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>

                      {/* Input */}
                      <div className="p-4 border-t">
                        <div className="flex gap-2">
                          <Input
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder={language === 'ar' ? 'اكتب رسالتك...' : 'Type your message...'}
                            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                          />
                          <Button className="btn-accent" onClick={sendMessage}>
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-muted-foreground">
                      <div className="text-center">
                        <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>{language === 'ar' ? 'اختر جهة اتصال للمراسلة' : 'Select a contact to message'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'ar' ? 'معلومات الحساب' : 'Account Information'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-10 h-10 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">{user?.full_name}</h3>
                    <p className="text-muted-foreground">@{user?.username}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                  <div>
                    <Label className="text-muted-foreground">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">{language === 'ar' ? 'الهاتف' : 'Phone'}</Label>
                    <p className="font-medium">{user?.phone || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ClientPortalPage;
